/**
 * What the deal's intake still owes it, on the deal itself.
 *
 * Stress test 2026-09-25:
 *   * OVR-01 / INT-08 — Riverbend Row's revised ask waited in Intake while the
 *     dossier said 'Request: $16M' with nothing to say a newer figure existed.
 *   * OVR-09 / CRE-26 — conflicts the platform knew about (rival sponsors, the
 *     sweep's arithmetic disagreements) appeared on no deal screen.
 *   * REG-06 / CMP-09 — the Data Intelligence Review and Request More Info could not be
 *     reached from a deal at all; both lived only on unpromoted records.
 *
 * The values themselves are decided where they always were — the record's
 * Email Thread panel and the Data Intelligence Review — and this strip is the way there.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ClipboardCheck, GitBranch, Send } from 'lucide-react'

import InfoRequestDialog from '@/components/intake/InfoRequestDialog'
import type { LoanDataConflict, LoanPendingRevision } from '@/types'
import { dateInputValue } from '@/utils/dates'
import { fmtRevisionValue, revisionLabel as label } from './revisionFormat'

export default function DealIntakeFlags({
  sourceIngestionId,
  pendingRevisions,
  dataConflicts,
  onLeave,
}: {
  sourceIngestionId?: string | null
  pendingRevisions: LoanPendingRevision[]
  dataConflicts: LoanDataConflict[]
  /** Close the dossier before leaving for Intake. */
  onLeave: () => void
}) {
  const navigate = useNavigate()
  const [infoOpen, setInfoOpen] = useState(false)

  const go = (ingestionId: string, review: boolean) => {
    onLeave()
    navigate(`/intake?focus=${encodeURIComponent(ingestionId)}${review ? '&review=1' : ''}`)
  }

  const revisionEmail = pendingRevisions.find((r) => r.ingestionId)?.ingestionId ?? null
  // Why they wait, as the engine recorded it — a confirmed value, an identity
  // field, a link only a person can vouch for. Not every wait is a contest
  // with a confirmed figure, so the strip does not claim one.
  const waitReasons = [...new Set(pendingRevisions.map((r) => r.why).filter(Boolean))]
  const emailDay = dateInputValue(pendingRevisions[0]?.receivedAt)
  const conflictFields = [...new Set(dataConflicts.map((c) => label(c.field)))]

  if (!sourceIngestionId && pendingRevisions.length === 0 && dataConflicts.length === 0) return null

  return (
    <div className="flex flex-shrink-0 flex-wrap items-start gap-2 border-b border-stone-800/80 px-3 py-2" data-testid="deal-intake-flags">
      {/* A floor width, not `min-w-0`: `flex-1` has a zero basis, so beside the
          shrink-0 button group on a phone the strip was left ~41px wide and read one
          word per line. With a floor it wraps to a row of its own. */}
      {pendingRevisions.length > 0 && (
        <div
          role="status"
          className="flex min-w-[min(100%,18rem)] flex-1 items-start gap-2 rounded border border-amber-400/30 bg-amber-400/[0.07] px-2.5 py-1.5 text-[11px] text-amber-100"
        >
          <GitBranch size={13} className="mt-px shrink-0 text-amber-300" />
          <div className="min-w-0">
            <div className="font-medium">
              {/* The lender's own draft is the desk's proposal, not a newer
                  statement of the deal (The Marlowe, A02). */}
              {pendingRevisions.every((r) => r.lenderDraft)
                ? 'Lender draft terms pending review'
                : `Newer figure${pendingRevisions.length === 1 ? '' : 's'} pending review`}
              {/* The desk's day, not the UTC stamp's: a 9:30pm New York email
                  sliced from UTC read as the next day (N2). */}
              {emailDay ? ` — email of ${emailDay}` : ''}
            </div>
            <div className="text-[10px] text-amber-100/80">
              {pendingRevisions.map((r) => (
                <span key={`${r.ingestionId}-${r.field}`} className="mr-3 inline-block">
                  {label(r.field)}: {fmtRevisionValue(r.field, r.current)} → {fmtRevisionValue(r.field, r.incoming)}
                </span>
              ))}
            </div>
            {waitReasons.length > 0 && (
              <div className="text-[10px] text-amber-100/70">Waiting: {waitReasons.join('; ')}</div>
            )}
            <div className="text-[10px] text-stone-400">
              The deal keeps its current value until someone applies or keeps the new one.
            </div>
          </div>
          {revisionEmail && (
            <button
              type="button"
              onClick={() => go(revisionEmail, false)}
              className="ml-auto shrink-0 rounded border border-amber-400/40 px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-400/10"
            >
              Review the email
            </button>
          )}
        </div>
      )}
      {dataConflicts.length > 0 && (
        <div className="flex min-w-[min(100%,18rem)] flex-1 items-start gap-2 rounded border border-red-400/25 bg-red-400/[0.06] px-2.5 py-1.5 text-[11px] text-red-100">
          <AlertTriangle size={13} className="mt-px shrink-0 text-red-300" />
          <div className="min-w-0">
            <div className="font-medium">
              {dataConflicts.length} open data question{dataConflicts.length === 1 ? '' : 's'}
            </div>
            <div className="text-[10px] text-red-100/80">{conflictFields.join(', ')}</div>
          </div>
        </div>
      )}
      {sourceIngestionId && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => go(sourceIngestionId, true)}
            title="Open the Data Intelligence Review for the package this deal was created from — decisions reach the deal"
            className="inline-flex h-7 items-center gap-1.5 rounded border border-stone-700 px-2.5 text-[11px] text-stone-300 hover:border-amber-300/40 hover:text-amber-200"
          >
            <ClipboardCheck size={12} /> Data Intelligence Review
          </button>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            title="Compose an internal missing-fields request — never sent to the broker"
            className="inline-flex h-7 items-center gap-1.5 rounded border border-stone-700 px-2.5 text-[11px] text-stone-300 hover:border-amber-300/40 hover:text-amber-200"
          >
            <Send size={11} /> Request More Info
          </button>
        </div>
      )}
      {infoOpen && sourceIngestionId && (
        <InfoRequestDialog ingestionId={sourceIngestionId} onClose={() => setInfoOpen(false)} />
      )}
    </div>
  )
}
